// Upper bound on the wait for an idle period, so a page that never goes idle
// (a long-running animation, a busy main thread) still gets the callback.
const IDLE_TIMEOUT_MS = 3000;

/**
 * Runs `callback` once the page has loaded and the main thread is idle, so
 * non-critical work (third-party trackers, prefetching code) doesn't compete
 * with hydration and the first render. Falls back to a timeout where `requestIdleCallback` is
 * missing (Safari). Returns a function that cancels the pending callback.
 */
export const whenPageIdle = (callback: () => void): (() => void) => {
	let cancelScheduled: (() => void) | undefined;

	const schedule = () => {
		if (typeof window.requestIdleCallback === 'function') {
			const handle = window.requestIdleCallback(callback, {
				timeout: IDLE_TIMEOUT_MS
			});
			cancelScheduled = () => window.cancelIdleCallback(handle);
		} else {
			const handle = window.setTimeout(callback, 1);
			cancelScheduled = () => window.clearTimeout(handle);
		}
	};

	if (document.readyState === 'complete') {
		schedule();
	} else {
		window.addEventListener('load', schedule, { once: true });
	}

	return () => {
		window.removeEventListener('load', schedule);
		cancelScheduled?.();
	};
};
